/**
 * IFRAP-AI - Archives CyberAgnès
 * 
 * Fonctionnalités:
 * - Affichage des chroniques passées avec catégories et dates
 * - Boutons de modification (angle, ton, chiffres, position IFRAP)
 * - Champ libre pour feedback
 * - Bouton publier sur IFRAP-AI (édito)
 * - Bouton envoyer à Économie Matin
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import api from '../../api/client';
import { 
  Archive, Send, Edit3, Check, X, RefreshCw, ExternalLink,
  AlertTriangle, MessageSquare, Newspaper, Globe, Clock, Mail,
  ThumbsDown, Zap, Hash, Ban, ChevronDown, ChevronUp, Loader2,
  Calendar, Tag, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const CyberAgnesArchivesPage = () => {
  const [chroniques, setChroniques] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [feedbackText, setFeedbackText] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [filterCategory, setFilterCategory] = useState(null);

  useEffect(() => {
    fetchChroniques();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChroniques = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/chronique/archives?limit=50');
      setChroniques(response.data.chroniques || []);
    } catch (error) {
      console.error('Error fetching chroniques:', error);
      toast.error('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/chronique/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(prev => ({ ...prev, [`${id}-${reason}`]: true }));
    try {
      const feedback = feedbackText[id] || '';
      const response = await api.post(`/api/email/cyberagnes/reject/${id}?reason=${reason}`, {
        feedback: feedback
      });
      
      if (response.data.success) {
        toast.success('Chronique rejetée - nouvelle version en cours de génération');
        // Refresh after a delay to get the new chronicle
        setTimeout(fetchChroniques, 5000);
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Erreur lors du rejet');
    } finally {
      setActionLoading(prev => ({ ...prev, [`${id}-${reason}`]: false }));
    }
  };

  const handlePublishIFRAP = async (chronique) => {
    setActionLoading(prev => ({ ...prev, [`${chronique.id}-publish`]: true }));
    try {
      const response = await api.post(`/api/chronique/publish-ifrap/${chronique.id}`);
      
      if (response.data.success) {
        toast.success('Chronique publiée sur IFRAP-AI !');
        fetchChroniques();
      }
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setActionLoading(prev => ({ ...prev, [`${chronique.id}-publish`]: false }));
    }
  };

  const handleSendEcoMatin = async (chronique) => {
    setActionLoading(prev => ({ ...prev, [`${chronique.id}-ecomatin`]: true }));
    try {
      const response = await api.post(`/api/chronique/send-economie-matin/${chronique.id}`);
      
      if (response.data.success) {
        toast.success('Chronique envoyée à Économie Matin !');
        fetchChroniques();
      }
    } catch (error) {
      console.error('Error sending to eco matin:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setActionLoading(prev => ({ ...prev, [`${chronique.id}-ecomatin`]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrer par catégorie
  const filteredChroniques = filterCategory 
    ? chroniques.filter(c => c.categories && c.categories.includes(filterCategory))
    : chroniques;

  const CategoryBadge = ({ category, info }) => (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${info?.color}20`, color: info?.color }}
    >
      <Tag className="w-3 h-3" />
      {info?.label || category}
    </span>
  );

  const RejectButton = ({ id, reason, icon: Icon, label, color }) => {
    const isLoading = actionLoading[`${id}-${reason}`];
    return (
      <button
        onClick={() => handleReject(id, reason)}
        disabled={isLoading}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${color} disabled:opacity-50`}
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
        {label}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement des archives...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Archive className="w-7 h-7 text-blue-600" />
            Archives CyberAgnès
          </h1>
          <p className="text-gray-500 mt-1">
            Consultez, modifiez et publiez les chroniques passées
          </p>
        </div>
        <button
          onClick={fetchChroniques}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Legend */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Télécommande Agnès</h3>
            <p className="text-sm text-amber-700 mt-1">
              Utilisez les boutons pour demander une nouvelle version ou ajouter votre feedback.
              Une nouvelle chronique sera générée automatiquement.
            </p>
          </div>
        </div>
      </div>

      {/* Filtres par catégorie */}
      {categories.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtrer par catégorie :</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                !filterCategory 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Toutes ({chroniques.length})
            </button>
            {categories.map(cat => {
              const count = chroniques.filter(c => c.categories?.includes(cat.id)).length;
              if (count === 0) return null;
              return (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(filterCategory === cat.id ? null : cat.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    filterCategory === cat.id
                      ? 'text-white'
                      : 'hover:opacity-80'
                  }`}
                  style={{ 
                    backgroundColor: filterCategory === cat.id ? cat.color : `${cat.color}20`,
                    color: filterCategory === cat.id ? 'white' : cat.color,
                  }}
                >
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chroniques List */}
      <div className="space-y-4">
        {filteredChroniques.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Archive className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">
              {filterCategory ? 'Aucune chronique dans cette catégorie' : 'Aucune chronique archivée'}
            </p>
          </div>
        ) : (
          filteredChroniques.map((chronique) => (
            <div 
              key={chronique.id}
              className={`bg-white border rounded-xl shadow-sm overflow-hidden transition-all ${
                chronique.rejected ? 'border-red-200 bg-red-50/30' : 
                chronique.published_ifrap ? 'border-green-200 bg-green-50/30' :
                'border-gray-200'
              }`}
            >
              {/* Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(expandedId === chronique.id ? null : chronique.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Date et statuts */}
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        <Calendar className="w-3 h-3" />
                        {formatDate(chronique.created_at)}
                      </span>
                      {chronique.time_str && (
                        <span className="text-xs text-gray-400">
                          à {chronique.time_str}
                        </span>
                      )}
                      {chronique.rejected && (
                        <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
                          Rejetée
                        </span>
                      )}
                      {chronique.published_ifrap && (
                        <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                          Publiée IFRAP
                        </span>
                      )}
                      {chronique.sent_economie_matin && (
                        <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                          Envoyée Éco Matin
                        </span>
                      )}
                    </div>
                    
                    {/* Titre */}
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {chronique.titre || 'Sans titre'}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                      {chronique.sujet || 'Pas de description'}
                    </p>
                    
                    {/* Catégories */}
                    {chronique.categories_info && chronique.categories_info.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {chronique.categories_info.map((cat, i) => (
                          <CategoryBadge key={chronique.categories[i] || `cat-${i}`} category={chronique.categories[i]} info={cat} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    {expandedId === chronique.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedId === chronique.id && (
                <div className="border-t border-gray-100">
                  {/* Chronique Text */}
                  <div className="p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Texte de la chronique
                    </h4>
                    <div 
                      className="prose prose-sm max-w-none text-gray-700 bg-white p-4 rounded-lg border"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(chronique.chronique_html || chronique.chronique_raw || 'Contenu non disponible') }}
                    />
                  </div>

                  {/* Feedback Field */}
                  <div className="p-4 border-t border-gray-100">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Edit3 className="w-4 h-4 inline mr-2" />
                      Votre feedback / Ce que vous auriez dit
                    </label>
                    <textarea
                      value={feedbackText[chronique.id] || ''}
                      onChange={(e) => setFeedbackText(prev => ({ ...prev, [chronique.id]: e.target.value }))}
                      placeholder="Expliquez ce qui ne va pas ou comment vous auriez formulé cette chronique..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows={3}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="p-4 bg-gray-50 border-t border-gray-100">
                    {/* Reject Buttons */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Demander une nouvelle version
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <RejectButton 
                          id={chronique.id} 
                          reason="angle_incorrect" 
                          icon={ThumbsDown} 
                          label="Angle incorrect"
                          color="bg-red-100 text-red-700 hover:bg-red-200"
                        />
                        <RejectButton 
                          id={chronique.id} 
                          reason="trop_mou" 
                          icon={Zap} 
                          label="Trop mou"
                          color="bg-orange-100 text-orange-700 hover:bg-orange-200"
                        />
                        <RejectButton 
                          id={chronique.id} 
                          reason="chiffres_faux" 
                          icon={Hash} 
                          label="Chiffres incorrects"
                          color="bg-purple-100 text-purple-700 hover:bg-purple-200"
                        />
                        <RejectButton 
                          id={chronique.id} 
                          reason="pas_ifrap" 
                          icon={Ban} 
                          label="Pas la position IFRAP"
                          color="bg-cyan-100 text-cyan-700 hover:bg-cyan-200"
                        />
                      </div>
                    </div>

                    {/* Publish/Send Buttons */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handlePublishIFRAP(chronique)}
                        disabled={actionLoading[`${chronique.id}-publish`] || chronique.published_ifrap}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                          chronique.published_ifrap 
                            ? 'bg-green-100 text-green-700 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {actionLoading[`${chronique.id}-publish`] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : chronique.published_ifrap ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Globe className="w-4 h-4" />
                        )}
                        {chronique.published_ifrap ? 'Publié sur IFRAP-AI' : 'Publier sur IFRAP-AI'}
                      </button>

                      <button
                        onClick={() => handleSendEcoMatin(chronique)}
                        disabled={actionLoading[`${chronique.id}-ecomatin`] || chronique.sent_economie_matin}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                          chronique.sent_economie_matin
                            ? 'bg-purple-100 text-purple-700 cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {actionLoading[`${chronique.id}-ecomatin`] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : chronique.sent_economie_matin ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Newspaper className="w-4 h-4" />
                        )}
                        {chronique.sent_economie_matin ? 'Envoyé à Éco Matin' : 'Envoyer à Économie Matin'}
                      </button>

                      {chronique.published_ifrap && (
                        <a
                          href={`/editos/${chronique.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-all"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Voir sur le site
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Sources */}
                  {chronique.sources && chronique.sources.length > 0 && (
                    <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources utilisées</h4>
                      <ul className="text-xs text-gray-500 space-y-1">
                        {chronique.sources.slice(0, 5).map((source, i) => (
                          <li key={`${chronique.id}-source-${i}`} className="truncate">• {source}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CyberAgnesArchivesPage;
